<template>
  <div>
    <h4>
      <button class="link link-dark" @click="resetActiveLevel">
        {{ $t('DESIGNS.CONFIGURATION.ALL_PRODUCTS') }}
      </button>
    </h4>
    <ul class="first-level">
      <li
        v-for="firstLevel in assortment"
        :key="firstLevel.id"
        class="first-level"
      >
        <h4>
          <button
            class="link link-dark icon-link icon-right"
            :class="{
              active: activeFirstLevel?.id === firstLevel.id,
            }"
            :disabled="isDeactivated(firstLevel)"
            @click="toggleFirstLevel(firstLevel)"
          >
            {{ compositionCategoryHelper.getCategoryName(firstLevel) }}
            <Icon icon="chevron" />
          </button>
        </h4>
        <ul
          v-if="
            firstLevel.children?.length &&
            activeFirstLevel?.id === firstLevel.id
          "
          class="second-level"
        >
          <li v-for="secondLevel in firstLevel.children" :key="secondLevel.id">
            <h4>
              <button
                class="link link-dark"
                :class="{
                  active: activeSecondLevel?.id === secondLevel.id,
                }"
                :disabled="isDeactivated(secondLevel)"
                @click="selectSecondLevel(secondLevel)"
              >
                {{ compositionCategoryHelper.getCategoryName(secondLevel) }}
              </button>
            </h4>
          </li>
        </ul>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import compositionCategoryHelper, {
  CategoriesTree,
  CompositionSpecificCategoriesTree,
  CompositionSpecificCategoryProductType,
} from 'commons/composition/compositionCategoryHelper';
import {
  applyFilters,
  ProductTypeFilters,
} from 'src/app/partnerarea/publishing/detail/productSettings/advanced/filters/productTypeFilters.ts';
import { computed } from 'vue';
import { getProductType } from '@spreadgroup/common-partner-lib/composition';

type ActiveLevel = Pick<CategoriesTree, 'id' | 'names'>;

const props = defineProps<{
  activeFirstLevel?: ActiveLevel;
  activeSecondLevel?: ActiveLevel;
  assortment: CompositionSpecificCategoriesTree[];
  productTypes: CompositionSpecificCategoryProductType[];
  filters: ProductTypeFilters;
}>();

const emit = defineEmits<{
  updateFirstLevel: [activeFirstLevelId?: ActiveLevel];
  updateSecondLevel: [activeSecondLevelId?: ActiveLevel];
}>();

const toggleFirstLevel = (category?: CategoriesTree) => {
  emit(
    'updateFirstLevel',
    category && props.activeFirstLevel?.id !== category.id
      ? { id: category.id, names: category.names }
      : undefined,
  );
  emit('updateSecondLevel', undefined);
};

const selectSecondLevel = (category?: CategoriesTree) => {
  emit(
    'updateSecondLevel',
    category && props.activeSecondLevel?.id !== category?.id
      ? { id: category.id, names: category.names }
      : undefined,
  );
};

const resetActiveLevel = () => {
  emit('updateFirstLevel', undefined);
  emit('updateSecondLevel', undefined);
};

const availableProductTypes = computed<
  CompositionSpecificCategoryProductType[]
>(() => applyFilters(props.productTypes, props.filters));

const isDeactivated = (category: CategoriesTree) => {
  return !availableProductTypes.value.some((productType) =>
    getProductType(category, productType.id),
  );
};
</script>

<style scoped lang="scss">
@import 'src/scss/styleguide/colors';
@import 'src/scss/styleguide/type';

h4 {
  margin: 0;
  padding: 0;
}

ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

li {
  margin: 0;

  & + li {
    margin-top: 16px;
  }
}

.first-level,
.second-level {
  button {
    text-align: left;
  }

  button:disabled {
    pointer-events: none;
    color: $grey40;
  }

  svg {
    height: 12px;
    width: 12px;
    transform: rotate(90deg);
  }

  button.active {
    color: $pa-color-main;
    font-weight: bold;

    svg {
      transform: rotate(270deg);
    }
  }
}

.first-level {
  margin-top: 16px;
}

.second-level {
  margin: 16px 0 0 16px;

  button {
    font-weight: 500;
  }
}
</style>
